export type StatusColumnLike = {
  type?: string | null;
  settings?: unknown;
  settings_str?: string | Record<string, unknown> | null;
  settingsStr?: string | Record<string, unknown> | null;
  column?: StatusColumnLike | null;
};

export type StatusColumnOption = {
  label: string;
  value: string;
};

function asSettingsRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function parseSettingsRaw(
  raw: string | Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  if (!raw) {
    return {};
  }
  if (typeof raw === 'object') {
    return asSettingsRecord(raw) ?? {};
  }

  try {
    return asSettingsRecord(JSON.parse(raw)) ?? {};
  } catch {
    return {};
  }
}

function settingsFromSource(
  source: StatusColumnLike | null | undefined,
): Record<string, unknown> {
  const fromObject = asSettingsRecord(source?.settings);
  if (fromObject?.labels != null) {
    return fromObject;
  }

  const fromRaw = parseSettingsRaw(source?.settings_str ?? source?.settingsStr);
  if (fromRaw.labels != null) {
    return fromRaw;
  }

  return fromObject ?? {};
}

export function readColumnSettings(
  column: StatusColumnLike | null | undefined,
): Record<string, unknown> {
  const fromColumn = settingsFromSource(column);
  if (fromColumn.labels != null) {
    return fromColumn;
  }
  return settingsFromSource(column?.column);
}

function isDeactivatedLabel(item: Record<string, unknown>): boolean {
  return item.is_deactivated === true || item.isDeactivated === true;
}

export function normalizeStatusLabels(labels: unknown): StatusColumnOption[] {
  if (!labels) {
    return [];
  }

  if (Array.isArray(labels)) {
    return labels
      .filter(
        (item): item is Record<string, unknown> =>
          !!item && typeof item === 'object' && !Array.isArray(item),
      )
      .filter((item) => !isDeactivatedLabel(item))
      .map((item) => {
        const text = typeof item.label === 'string' ? item.label : '';
        return { label: text, value: text };
      })
      .filter((option) => option.label);
  }

  if (typeof labels === 'object') {
    return Object.values(labels as Record<string, unknown>)
      .filter((value): value is string => typeof value === 'string' && !!value)
      .map((text) => ({ label: text, value: text }));
  }

  return [];
}

export function parseStatusColumnOptions(
  column: StatusColumnLike | null | undefined,
): StatusColumnOption[] {
  if (column?.type !== 'status' && column?.column?.type !== 'status') {
    return [];
  }

  return normalizeStatusLabels(readColumnSettings(column).labels);
}

export function resolveStatusFilterOptions(input: {
  column?: StatusColumnLike | null;
  fallbackValues?: Array<string | null | undefined>;
}): StatusColumnOption[] {
  const fromColumn = parseStatusColumnOptions(input.column);
  if (fromColumn.length > 0) {
    return fromColumn;
  }

  const unique = new Set<string>();
  for (const value of input.fallbackValues ?? []) {
    const text = String(value ?? '').trim();
    if (text) unique.add(text);
  }
  return [...unique].map((text) => ({ label: text, value: text }));
}
